import {
  ACESFilmicToneMapping,
  Color,
  Fog,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  SRGBColorSpace,
  Vector2,
  WebGLRenderer,
} from 'three';
import {
  BloomEffect,
  EffectComposer,
  EffectPass,
  RenderPass,
  SMAAEffect,
  SMAAPreset,
  VignetteEffect,
} from 'postprocessing';
import { Time } from './time';
import { Emitter } from '@/utils/events';
import type { AppEvents } from './types';
import type { System } from './system';
import { PALETTE } from '@/config';
import { IS_TOUCH } from '@/utils/device';

/**
 * The Engine owns the three "physical" resources — renderer, scene, camera — plus
 * the post-processing composer and the frame loop. Everything else in the game is
 * a System registered here. It intentionally knows nothing about the city, the AI
 * or the player; those are supplied from main.ts. This keeps the engine reusable
 * and the game logic testable in isolation.
 *
 * Post-processing is what sells the "art installation" look: a soft bloom for the
 * pale light, gentle vignette to frame the composition, and SMAA so the low-poly
 * silhouettes stay crisp without the cost of MSAA on the whole buffer.
 */
export class Engine {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly composer: EffectComposer;
  readonly time = new Time();
  readonly events = new Emitter<AppEvents>();

  readonly bloom: BloomEffect;
  readonly vignette: VignetteEffect;

  /** Becomes true the first time the player enters (locks the pointer). The AI
   *  holds still until then — the city only begins to learn once you are in it. */
  hasEntered = false;

  private systems: System[] = [];
  private running = false;
  private rafId = 0;
  private readonly resolution = new Vector2();

  constructor(private container: HTMLElement) {
    this.renderer = new WebGLRenderer({
      antialias: false, // SMAA handles AA in the composer.
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
    });
    // Touch GPUs are far tighter on fill-rate; cap the pixel ratio lower there.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_TOUCH ? 1.5 : 2));
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.scene = new Scene();
    this.scene.background = new Color(PALETTE.sky);
    // Fog is a core aesthetic tool here: it dissolves the city edges into paper,
    // hides pop-in of newly grown districts, and is animated by the mood system.
    this.scene.fog = new Fog(PALETTE.fog, 40, 260);

    this.camera = new PerspectiveCamera(62, this.aspect, 0.1, 800);
    this.camera.position.set(0, 1.7, 0);

    // --- Post processing -------------------------------------------------
    this.composer = new EffectComposer(this.renderer, {
      frameBufferType: undefined,
    });
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.bloom = new BloomEffect({
      intensity: 0.55,
      luminanceThreshold: 0.72,
      luminanceSmoothing: 0.28,
      mipmapBlur: true,
    });
    this.vignette = new VignetteEffect({ darkness: 0.42, offset: 0.32 });
    const smaa = new SMAAEffect({ preset: SMAAPreset.HIGH });
    this.composer.addPass(new EffectPass(this.camera, smaa, this.bloom, this.vignette));

    this.onResize();
    // viewport.ts re-lays the stage on these same events (it registers first, so it
    // runs first); the engine then reads the freshly-sized container. visualViewport
    // covers iOS URL-bar changes that don't always fire a window resize.
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.visualViewport?.addEventListener('resize', this.onResize);
  }

  get aspect(): number {
    return this.container.clientWidth / Math.max(1, this.container.clientHeight);
  }

  add<T extends System>(system: T): T {
    this.systems.push(system);
    return system;
  }

  /** Retrieve a registered system by name (used sparingly for cross-wiring). */
  get<T extends System>(name: string): T | undefined {
    return this.systems.find((s) => s.name === name) as T | undefined;
  }

  start(): void {
    this.events.on('app:pointerlock', ({ locked }) => {
      if (locked) this.hasEntered = true;
    });
    for (const s of this.systems) s.init?.(this);
    this.running = true;
    this.loop(performance.now());
    // Defer the ready signal one frame so the first render is on screen.
    requestAnimationFrame(() => this.events.emit('app:ready', undefined));
  }

  private loop = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.loop);
    this.time.tick(now);
    for (const s of this.systems) s.update?.(this.time);
    for (const s of this.systems) s.lateUpdate?.(this.time);
    this.composer.render(this.time.delta);
  };

  private onResize = (): void => {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.resolution.set(w, h);
    this.camera.aspect = w / Math.max(1, h);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.composer.setSize(w, h);
  };

  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.visualViewport?.removeEventListener('resize', this.onResize);
    for (const s of this.systems) s.dispose?.();
    this.composer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.events.clear();
  }
}
